import { Style, Circle, Stroke, Fill, Icon, Text, RegularShape } from 'ol/style'
import MultiPoint from 'ol/geom/MultiPoint.js'
import { Polygon, LineString } from 'ol/geom'

import { getWorkPolygonFeatureTypes, isElevated, isGrounded } from '@/models/shapes'
import { getTechRoomColor } from '@/models/techRooms'

import { FEATURE_TYPES as floorFeatures } from '@/utils/plan'
import { FEATURE_TYPES as facadeFeatures } from '@/utils/facade'
import { TASK_TYPES } from '@/utils/tasks'
import { toRadians } from '@/utils/math'
import { hexToRgba } from '@/utils/colors'
import { getParallelSegment } from '@/utils/ui'

import copterIcon from '@/assets/copter.svg'
import borderedCopterIcon from '@/assets/copter_bordered.svg'
import sightIcon from '@/assets/sight.svg'
import visionDefectIcon from '@/assets/vectors/vision_defect.svg'
import pointBack from '@/assets/point_back.svg'
import pointBackRect from '@/assets/point_back_rect.svg'

const colors = {
  accent: '#5A9DF8',
  blue: '#007AFF',
  white: '#FFFFFF',
  black: '#000000',
  grey: '#909399',
  rock: '#414141',
  red: '#E47470',
  transparent: 'transparent'
}

const values = {
  selectionWidth: 2
}

const self = {
  floor: {
    [floorFeatures.CAMERA360]: ({ feature, selected, rotated, rotation, origin }) => [
      new Style({
        image: new Circle({
          radius: selected ? 16 : 12,
          fill: new Fill({ 
            color: isElevated(feature) ? colors.white 
              : isGrounded(feature) ? colors.rock : colors.blue
          }),
          ...selected && { stroke: new Stroke({ color: isElevated(feature) ? colors.blue : colors.white, width: values.selectionWidth }) }
        }),
        fill: new Fill({color: 'rgba(0, 0, 255, 0.1)'}),
        text: self['label']({ feature, color: isElevated(feature) && colors.blue }),
        zIndex: 4
      }),
              
      feature.get('task_defects_active_count') && new Style({
        image: new Circle({
          radius: selected ? 7 : 5,
          fill: new Fill({
            color: colors.red
          }),
          displacement: selected ? [10, -10] : [9, -9]
        }),
        zIndex: 5
      }),

      new Style({
        image: new Icon({
          src: pointBack,
          crossOrigin: 'anonymous',
          imgSize: [33, 33],
          scale: selected ? 1.5 : 1
        }),
        zIndex: 3
      }),

      self['sight']({ feature, selected, rotated, rotation, origin })
    ].filter(is),

    [floorFeatures.QUADCOPTER]: ({ feature, selected, rotated, rotation, origin }) => [
      new Style({
        image: new Icon({
          color: '#909399',
          crossOrigin: 'anonymous',
          imgSize: [120, 120],
          scale: selected ? 0.3 : 0.28,
          src: selected ? borderedCopterIcon : copterIcon
        }),
        fill: new Fill({color: 'rgba(0, 0, 255, 0.1)'}),
        stroke: new Stroke({
          color: '#ffcc33',
          width: 2
        }),
        text: self['label']({ feature }),
        zIndex: 4
      }),
      self['sight']({ feature, selected, rotated, rotation, origin })
    ],

    [floorFeatures.FACADE]: ({ feature, selected, rotated, rotation, origin }) => [
      new Style({
        image: new Icon({
          color: colors.red,
          crossOrigin: 'anonymous',
          imgSize: [120, 120],
          scale: 0.28,
          src: selected ? borderedCopterIcon : copterIcon
        }),
        text: self['label']({ feature }),
        zIndex: 4
      }),
      self['sight']({ feature, selected, rotated, rotation, origin })
    ],

    [floorFeatures.DEFECT]: ({ feature, selected, rotated, rotation, origin }) => [
      new Style({
        image: new RegularShape({
          fill: new Fill({ color: colors.red }),
          ...selected && { stroke: new Stroke({ color: colors.white, width: values.selectionWidth }) },
          points: 4,
          radius: selected ? 23 : 15,
          angle: Math.PI / 4
        }),
        text: self['label']({ feature }),
        zIndex: 4
      }),

      new Style({
        image: new Icon({
          src: pointBackRect,
          crossOrigin: 'anonymous',
          imgSize: [33, 33],
          scale: selected ? 1.5 : 1
        }),
        zIndex: 3
      }),

      self['sight']({ feature, selected, rotated, rotation, origin })
    ],

    [floorFeatures.MONITOR]: ({ feature, selected }) => [
      new Style({
        image: new Circle({
          radius: 12,
          fill: new Fill({ 
            color: selected ? colors.white : colors.blue
          })
        }),
        text: self['label']({ feature, selected, selectedColor: colors.blue }),
        zIndex: 4
      }),

      new Style({
        image: new Icon({
          src: pointBack,
          crossOrigin: 'anonymous',
          imgSize: [33, 33]
        }),
        zIndex: 3
      }),

      // Стили для линии направления точки мониторинга
      new Style({
        fill: new Fill({ color: colors.blue }),
        stroke: new Stroke({ color: colors.blue, width: 3 }),
        zIndex: 4,
        geometry: () => {
          const coordsLine = feature.get('projection')?.vector
  
          if (coordsLine) {
            return new LineString(coordsLine)
          }
  
          return null
        }
      }),

      // Стили для угла направления точки мониторинга
      new Style({
        fill: new Fill({ color: hexToRgba(colors.black, 0.5) }),
        zIndex: 4,
        geometry: () => {
          const coordsPoint = feature.getGeometry().getCoordinates()
          const coordsLine = feature.get('projection')?.vector

          if (coordsLine) {
            const coordsViewPolygon = [ coordsPoint, ...coordsLine, coordsPoint ]

            return new Polygon([ coordsViewPolygon ])
          }

          return null
        }
      }),

      // Стили для области точки мониторинга
      new Style({
        fill: new Fill({ color: hexToRgba(colors.blue, 0.25) }),
        zIndex: 4,
        geometry: () => {
          if (selected && feature.get('projection')) {
            const coordsPoint = feature.getGeometry().getCoordinates()
            const coordsLine = feature.get('projection')?.vector

            const coordsAreaPolygon = [
              ...coordsLine, 
              ...getParallelSegment(coordsLine, 20, coordsPoint),
              coordsLine[0]
            ]

            return new Polygon([ coordsAreaPolygon ])
          }

          return null
        }
      })
    ],

    [floorFeatures.PROTOCOL]: () => new Style({
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        width: 3
      }),
      fill: new Fill({color: 'rgba(0, 0, 0, 0.5)'}),
      text: new Text({
        font: '16px sans-serif',
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          width: 3
        }),
        fill: new Fill({color: '#fefff9'})
      })
    }),

    [floorFeatures.ROOM]: () => new Style({
      stroke: new Stroke({
        color: 'rgba(0, 0, 0, 0.5)',
        width: 3
      }),
      fill: new Fill({color: 'rgba(0, 0, 0, 0.5)'}),
      text: new Text({
        font: '16px sans-serif',
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          width: 3
        }),
        fill: new Fill({color: '#fefff9'})
      })
    }),

    [floorFeatures.TECH_ROOM]: ({ feature } = {}) => {
      const color = feature && getTechRoomColor(feature.get('techRoom'))
      
      return new Style({
        stroke: new Stroke({ color: color || 'rgba(0, 0, 0, 0.5)', width: 3 }),
        fill: new Fill({ color: color && hexToRgba(color) || 'rgba(0, 0, 0, 0.5)' })
      })
    },

    [floorFeatures.RULER_CALIBRATION]: () => new Style({
      fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
      stroke: new Stroke({
        color: '#f56c6c',
        width: 3,
        lineDash: [10, 10]
      }),
      text: new Text({
        offsetY: 2,
        font: '16px Tahoma',
        fill: new Fill({color: '#000000'})
      })
    }),

    [floorFeatures.RULER_LENGTH]: () => new Style({
      fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
      stroke: new Stroke({
        color: '#f56c6c',
        width: 3
      }),
      text: new Text({
        offsetY: 2,
        font: '16px Tahoma',
        fill: new Fill({color: '#000000'})
      })
    }),

    [floorFeatures.RULER_AREA]: () => new Style({
      fill: new Fill({color: 'rgba(255, 255, 255, 0.2)'}),
      stroke: new Stroke({
        color: '#f56c6c',
        width: 3
      }),
      text: new Text({
        offsetY: 2,
        font: '16px Tahoma',
        fill: new Fill({color: '#000000'})
      })
    }),

    [floorFeatures.RULER_ORIENTATION]: () => new Style({
      fill: new Fill({ color: 'rgba(255, 255, 255, .2)' }),
      stroke: new Stroke({
        color: colors.accent,
        width: 6
      }),
      text: new Text({
        offsetY: 2,
        font: '16px Tahoma',
        fill: new Fill({ color: '#000000' })
      })
    }),

    [floorFeatures.INTERMEDIATE_ACCEPTANCE]: () => new Style({
      stroke: new Stroke({
        color: 'rgb(230, 162, 60)',
        width: 4
      }),
      fill: new Fill({ color: 'rgba(230, 162, 60, 0.4)' })
    }),

    [floorFeatures.FINAL_ACCEPTANCE]: () => new Style ({
      stroke: new Stroke({
        color: 'rgb(103, 194, 58)',
        width: 4
      }),
      fill: new Fill({ color: 'rgba(103, 194, 58, 0.4)' })
    }),

    [floorFeatures.INTERMEDIATE_ACCEPTANCE_OLD]: () => new Style({
      stroke: new Stroke({
        color: 'rgb(230, 162, 60)',
        width: 1
      }),
      fill: new Fill({ color: 'rgba(230, 162, 60, 0.1)' })
    }),

    [floorFeatures.FINAL_ACCEPTANCE_OLD]: () => new Style({
      stroke: new Stroke({
        color: 'rgb(103, 194, 58)',
        width: 1
      }),
      fill: new Fill({ color: 'rgba(103, 194, 58, 0.1)' })
    }),

    ...getWorkPolygonFeatureTypes().map(type => ({
      type,
      color: ({
        [floorFeatures.WORK_POLYGON_NEW]: [243, 106, 110],
        [floorFeatures.WORK_POLYGON_CHANGED]: [242, 153, 74],
        [floorFeatures.WORK_POLYGON_UNCHANGED]: [39, 174, 96],
        [floorFeatures.WORK_POLYGON_CREATED]: [64, 158, 255],
        [floorFeatures.WORK_POLYGON_AT_WORK]: [93, 95, 239],
        [floorFeatures.WORK_POLYGON_COMPLETED]: [39, 174, 96],
        [floorFeatures.WORK_POLYGON_COMPLETED_PARTIALLY]: [242, 153, 74],
        [floorFeatures.WORK_POLYGON_WITH_PROBLEM]: [235, 87, 87],
        [floorFeatures.WORK_POLYGON_WITH_WARNING]: [187, 107, 217],
        [floorFeatures.WORK_POLYGON_ACCEPTANCE_CONFIRMED]: [39, 174, 96],
        [floorFeatures.WORK_POLYGON_ACCEPTANCE_REJECTED]: [235, 87, 87],
        [floorFeatures.WORK_POLYGON_ACCEPTANCE_DEFAULT]: [236, 174, 72],
        [floorFeatures.WORK_POLYGON_ACCEPTANCE_NONE]: [64, 158, 255]
      })[type]
    }))
      .filter(x => x.color)
      .reduce((r, { type, color }) => ({
        ...r,
        [type]: () => new Style({
          stroke: new Stroke({ color: `rgb(${color.join(', ')})`, width: 1 }),
          fill: new Fill({ color: `rgba(${color.join(', ')}, 0.5)` })
        })
      }), {})
  },

  markup: {
    [facadeFeatures.PERSPECTIVE]: () => new Style({
      stroke: new Stroke({
        color: 'rgba(228, 116, 112, 0.5)',
        width: 3
      }),
      fill: new Fill({color: 'rgba(228, 116, 112, 0.5)'})
    }),

    [facadeFeatures.SHAPE]: () => new Style({
      stroke: new Stroke({
        color: 'transparent',
        width: 1
      })
    }),

    [facadeFeatures.FACADE]: ({ selected }) => new Style({
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: selected ? colors.black : 'rgb(31,120,180)',
          width: 1
        }),
        fill: new Fill({color: 'rgb(31,120,180)'})
      })
    }),

    [facadeFeatures.INSULATION]: ({ selected }) => new Style({
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: selected ? colors.black : 'rgb(254,127,14)',
          width: 1
        }),
        fill: new Fill({color: 'rgb(254,127,14)'})
      })
    }),

    [facadeFeatures.WALL]: ({ selected }) => new Style({
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: selected ? colors.black : 'rgb(43,160,45)',
          width: 1
        }),
        fill: new Fill({color: 'rgb(43,160,45)'})
      })
    }),

    [facadeFeatures.GLASS]: ({ selected }) => new Style({
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: selected ? colors.black : '#8E44AD',
          width: 1
        }),
        fill: new Fill({ color: '#8E44AD' })
      })
    }),

    [facadeFeatures.VOID]: ({ selected }) => new Style({
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: selected ? colors.black : '#16A085',
          width: 1
        }),
        fill: new Fill({ color: '#16A085' })
      })
    }),

    default: () => new Style({
      stroke: new Stroke({
        color: 'red',
        width: 3
      }),
      fill: new Fill({color: 'rgba(255, 0, 0, 0.1)'}),
      image: new Circle({
        radius: 6,
        stroke: new Stroke({
          color: 'rgb(31,120,180)',
          width: 1
        }),
        fill: new Fill({color: 'rgb(31,120,180)'})
      })
    }),

    mask: () => new Fill({
      color: 'rgba(0, 0, 0, 0.6)'
    })
  },

  monitor: {
    [TASK_TYPES.DEFECTS_AND_VIOLATIONS]: () => [
      new Style({
        stroke: new Stroke({
          color: 'rgba(245, 108, 108, 1)',
          width: 1
        }),
        fill: new Fill({ color: 'rgba(245, 108, 108, 0.2)' })
      }),
      new Style({
        image: new Circle({
          radius: 3,
          fill: new Fill({
            color: 'rgba(245, 108, 108, 1)'
          })
        }),
        geometry: feature => new MultiPoint(feature.getGeometry().getCoordinates()[0])
      })
    ],

    'vision': ({ feature }) => [
      new Style({
        fill: new Fill({ color: 'rgba(200, 201, 74, 0.2)' }),
        text: new Text({
          text: feature.get('label'),
          font: '1.5em sans-serif',
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, .5)',
            width: 3
          }),
          fill: new Fill({ color: '#ffffff' })
        })
      }),
      new Style({
        image: new Icon({
          src: visionDefectIcon,
          opacity: feature.get('hasDefects') ? 1 : 0,
          width: 20,
          height: 20,
          displacement: [16, 16]
        }),
        geometry: feature => feature.getGeometry().getInteriorPoint()
      })
    ]
  },

  label: ({ feature, selected, selectedColor, color, offsetY = 0 }) => new Text({
    text: feature.get('name'),
    offsetY: 1 + offsetY,
    font: 'bold 13px sans-serif',
    fill: new Fill({ color: selected ? selectedColor || colors.accent : color || colors.white })
  }),

  sight: ({ selected, rotated, rotation, origin }) => new Style({
    image: new Icon({
      src: sightIcon,
      scale: rotated && selected ? 1 : 0,
      rotation: rotated ? toRadians(90) - (origin - rotation) : 0,
      anchor: [.47, .8]
    })
  }),

  cluster: ({ feature }) => new Style({
    image: new Circle({
      radius: 20,
      stroke: new Stroke({
        color: '#409EFF',
        width: 3
      }),
      fill: new Fill({color: '#fff'})
    }),
    text: new Text({
      offsetY: 1,
      text: (feature.get('features') || []).filter(feature => !feature.get('_ignored')).length + '',
      font: 'bold 18px sans-serif',
      fill: new Fill({color: '#494A4D'})
    })
  })
}

export default self
